import { useTranslation } from "react-i18next";

import type { CcObjectGraph } from "@/api/code-companion";
import { useCcGraph } from "@/hooks/useCodeCompanion";

/**
 * A ordem em que o pacote é construído, e por quê (S-064 onda 6).
 *
 * A ordem topológica já era calculada e as **arestas** eram descartadas. Ordem sem
 * aresta responde "em que sequência" e não responde "por quê": um objeto no fim da fila
 * pode estar lá por dependência ou por acaso do desempate, e quem lê não distingue.
 *
 * **A ordem de rollback importa tanto quanto a de build.** Desfazer na ordem errada
 * deixa objeto ativo apontando para objeto que já não existe, que no ABAP Cloud é o
 * estado mais difícil de sair. Ela é a de build ao contrário — e **some quando há
 * ciclo**, porque uma ordem parcial invertida seria um plano que não fecha.
 *
 * Uma lista, não um desenho. O grafo de um pacote RAP tem 4 a 7 nós numa cadeia quase
 * linear; um canvas custaria uma dependência de layout para mostrar uma fila.
 */

/** De quem cada objeto depende, na forma que a lista precisa. */
export function dependenciesOf(graph: CcObjectGraph, node: string): string[] {
  return graph.edges.filter((e) => e.from === node).map((e) => e.to);
}

export default function BuildOrderPanel({
  projectId,
  packageId,
}: {
  projectId: string;
  packageId: number;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useCcGraph(projectId, packageId);

  if (isLoading || !data) return null;

  // Pacote sem dependência declarada não tem ordem a explicar: a fila é arbitrária e
  // fingir que é um plano seria pior que não mostrar.
  if (!data.edges.length) return null;

  return (
    <div
      data-testid="build-order"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Build order")}
        </span>
        {data.cycle && (
          <span
            data-testid="graph-cycle"
            className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
            style={{ background: "#fdf2f2", color: "#b02a31" }}
          >
            {t("cycle: {{objects}}", { objects: data.cycle_nodes.join(", ") })}
          </span>
        )}
      </div>

      <ol className="divide-y divide-border">
        {data.build_order.map((n, i) => {
          const deps = dependenciesOf(data, n);
          return (
            <li key={n} className="flex flex-wrap items-baseline gap-2 px-3 py-1.5 text-[11px]">
              <span className="w-5 font-mono text-[10px] text-muted-foreground">{i + 1}</span>
              <span className="font-mono">{n}</span>
              {/* O "por quê" da posição na fila. Sem isto a lista é só uma ordem. */}
              {deps.length > 0 && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {t("after")} {deps.join(", ")}
                </span>
              )}
            </li>
          );
        })}
      </ol>

      <div className="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
        {data.rollback_order.length ? (
          <span data-testid="rollback-order" className="font-mono">
            {t("rollback")} {data.rollback_order.join(" → ")}
          </span>
        ) : (
          // Dito, não omitido: quem for desfazer precisa saber que não há plano.
          <span data-testid="rollback-unknown">
            {t("no reliable rollback order while there is a cycle")}
          </span>
        )}
      </div>
    </div>
  );
}
